import { getCurrentMonth, getCurrentYear } from 'common-react';

import {
  MAX_AGE,
  MIN_AGE,
  MIN_MONTHLY_GROSS_SALARY,
  MONTHS_IN_YEAR,
  RetirementCalculatorField,
} from './RetirementCalculator.const';
import {
  RetirementCalculatorErrorCode,
  RetirementCalculatorErrorCodesModel,
  RetirementCalculatorFormValuesModel,
} from './RetirementCalculator.model';

export const getAgeFromBirthYear = (birthYear: number, currentYear = getCurrentYear()) =>
  currentYear - birthYear;

export const isAgeInRange = (birthYear: number, currentYear = getCurrentYear()) => {
  const age = getAgeFromBirthYear(birthYear, currentYear);
  return age >= MIN_AGE && age <= MAX_AGE;
};

export const getRemainingMonthsInYear = (currentMonth = getCurrentMonth()) =>
  MONTHS_IN_YEAR - currentMonth + 1;

export const getYearlyLimitForecast = (
  monthlyContribution: number | undefined,
  yearlyLimit: number | undefined,
  remainingMonths = getRemainingMonthsInYear(),
) => {
  if (!monthlyContribution || yearlyLimit === undefined) {
    return undefined;
  }

  const forecast = monthlyContribution * remainingMonths;

  return {
    forecast,
    remainingMonths,
    yearlyLimit,
    isExceeded: forecast > yearlyLimit,
    monthlyLimit: remainingMonths > 0 ? yearlyLimit / remainingMonths : undefined,
  };
};

const validateSalary = (salary?: number) => {
  if (salary === undefined) {
    return RetirementCalculatorErrorCode.REQUIRED;
  }
  return salary < MIN_MONTHLY_GROSS_SALARY
    ? RetirementCalculatorErrorCode.SALARY_BELOW_MIN
    : undefined;
};

const validateBirthYear = (birthYear: number | undefined, currentYear: number) => {
  if (birthYear === undefined) {
    return RetirementCalculatorErrorCode.REQUIRED;
  }
  return isAgeInRange(birthYear, currentYear)
    ? undefined
    : RetirementCalculatorErrorCode.AGE_OUT_OF_RANGE;
};

export const validateRetirementCalculatorForm = (
  values: RetirementCalculatorFormValuesModel,
  currentYear = getCurrentYear(),
): RetirementCalculatorErrorCodesModel => {
  const errors: RetirementCalculatorErrorCodesModel = {};

  const salaryError = validateSalary(values[RetirementCalculatorField.MONTHLY_GROSS_SALARY]);
  if (salaryError) {
    errors[RetirementCalculatorField.MONTHLY_GROSS_SALARY] = salaryError;
  }

  if (!values[RetirementCalculatorField.EMPLOYMENT_TYPE]) {
    errors[RetirementCalculatorField.EMPLOYMENT_TYPE] = RetirementCalculatorErrorCode.REQUIRED;
  }

  const birthYearError = validateBirthYear(values[RetirementCalculatorField.BIRTH_YEAR], currentYear);
  if (birthYearError) {
    errors[RetirementCalculatorField.BIRTH_YEAR] = birthYearError;
  }

  if (!values[RetirementCalculatorField.GENDER]) {
    errors[RetirementCalculatorField.GENDER] = RetirementCalculatorErrorCode.REQUIRED;
  }

  return errors;
};

export const formatAmount = (value: number) =>
  String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

const NICE_STEP_MULTIPLIERS = [1, 2, 2.5, 5];
const MAX_TICK_COUNT = 6;
const MAX_MAGNITUDE_STEPS = 12;

const buildTicks = (step: number, count: number) =>
  Array.from({ length: count + 1 }, (_unused, index) => step * index);

export const getChartScaleTicks = (
  value: number | undefined,
  minScaleMax: number,
  defaultSteps: number,
) => {
  if (!value || value <= minScaleMax) {
    return buildTicks(minScaleMax / defaultSteps, defaultSteps);
  }

  let magnitude = 10 ** Math.floor(Math.log10(value / MAX_TICK_COUNT));

  for (let attempt = 0; attempt < MAX_MAGNITUDE_STEPS; attempt += 1) {
    for (const multiplier of NICE_STEP_MULTIPLIERS) {
      const step = multiplier * magnitude;
      const count = Math.ceil(value / step);

      if (count <= MAX_TICK_COUNT) {
        return buildTicks(step, count);
      }
    }
    magnitude *= 10;
  }

  return buildTicks(value / defaultSteps, defaultSteps);
};

export const sanitizeBirthYearInput = (rawValue: string, maxLength: number) =>
  rawValue.replace(/\D/g, '').slice(0, maxLength);
